import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { safeLocalStorage } from '@/lib/safeStorage';
import { hexToHsl, adjustHslLightness } from '@/lib/utils';
import vendasLogo from '@/assets/vendasgroup.png';

interface TenantBranding {
  system_name?: string;
  login_title?: string;
  login_subtitle?: string;
  favicon_url?: string;
  footer_text?: string;
  // Campos para logos por tema
  logo_light_url?: string | null;
  logo_dark_url?: string | null;
  logo_scale?: number;
  logo_position_x?: number;
  logo_position_y?: number;
  // White-label Fase 2
  login_background_url?: string | null;
  default_theme?: 'light' | 'dark' | 'system';
  welcome_message?: string;
  support_email?: string;
  support_whatsapp?: string;
  support_url?: string;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  branding: TenantBranding;
}

interface TenantContextType {
  tenant: Tenant | null;
  tenantId: string | null;
  isLoading: boolean;
  isDefaultTenant: boolean;
  error: string | null;
}

// Valores padrão para Vendas.Group
const DEFAULT_TENANT: Tenant = {
  id: 'default',
  name: 'Vendas.Group',
  slug: 'vendas-group',
  logoUrl: vendasLogo,
  primaryColor: null,
  secondaryColor: null,
  branding: {
    system_name: 'Vendas.Group',
    login_title: 'Vendas.Group',
    login_subtitle: 'Plataforma completa para gestão e crescimento de vendas',
  },
};

const TenantContext = createContext<TenantContextType>({
  tenant: null,
  tenantId: null,
  isLoading: true,
  isDefaultTenant: true,
  error: null,
});

const TENANT_CACHE_KEY = 'jnr-sales-tenant-cache';
const TENANT_CACHE_EXPIRY_KEY = 'jnr-sales-tenant-cache-expiry';
const TENANT_CACHE_DURATION = 60 * 60 * 1000; // 1 hora em ms

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDefaultTenant, setIsDefaultTenant] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const detectTenant = async () => {
      try {
        const hostname = window.location.hostname;

        // Ignorar domínios de desenvolvimento/preview
        const isDevDomain = 
          hostname === 'localhost' ||
          hostname.includes('127.0.0.1') ||
          hostname.includes('lovable.app') ||
          hostname.includes('lovableproject.com') ||
          hostname.includes('preview');

        if (isDevDomain) {
          setTenant(DEFAULT_TENANT);
          setIsDefaultTenant(true);
          setIsLoading(false);
          return;
        }

        // OTIMIZAÇÃO PERFORMANCE: Verificar cache primeiro
        try {
          const cachedTenantStr = safeLocalStorage.getItem(TENANT_CACHE_KEY);
          const cacheExpiry = safeLocalStorage.getItem(TENANT_CACHE_EXPIRY_KEY);
          
          if (cachedTenantStr && cacheExpiry) {
            const expiryTime = parseInt(cacheExpiry, 10);
            const now = Date.now();
            
            if (now < expiryTime) {
              // Cache válido - usar dados do cache
              const cachedTenant = JSON.parse(cachedTenantStr) as Tenant;
              setTenant(cachedTenant);
              setIsDefaultTenant(cachedTenant.id === 'default');
              setIsLoading(false);
              return;
            } else {
              // Cache expirado - limpar
              safeLocalStorage.removeItem(TENANT_CACHE_KEY);
              safeLocalStorage.removeItem(TENANT_CACHE_EXPIRY_KEY);
            }
          }
        } catch (cacheError) {
          // Ignorar erros de cache e prosseguir com fetch
          console.debug('[TenantContext] Cache error, fetching fresh data:', cacheError);
        }

        // Consultar banco pelo domínio
        const { data, error: rpcError } = await supabase.rpc('get_tenant_by_domain', {
          p_domain: hostname,
        });

        if (rpcError) {
          setError(rpcError.message);
          setTenant(DEFAULT_TENANT);
          setIsDefaultTenant(true);
          setIsLoading(false);
          return;
        }

        if (data && data.length > 0) {
          const tenantData = data[0];
          
          const loadedTenant: Tenant = {
            id: tenantData.id,
            name: tenantData.name,
            slug: tenantData.slug,
            logoUrl: tenantData.logo_url,
            primaryColor: tenantData.primary_color,
            secondaryColor: tenantData.secondary_color,
            branding: (tenantData.branding as TenantBranding) || {},
          };
          
          // OTIMIZAÇÃO PERFORMANCE: Salvar no cache
          try {
            safeLocalStorage.setItem(TENANT_CACHE_KEY, JSON.stringify(loadedTenant));
            safeLocalStorage.setItem(TENANT_CACHE_EXPIRY_KEY, String(Date.now() + TENANT_CACHE_DURATION));
          } catch (storageError) {
            // Ignorar erros de localStorage (pode estar desabilitado)
            console.debug('[TenantContext] Could not save to cache:', storageError);
          }
          
          setTenant(loadedTenant);
          setIsDefaultTenant(false);
        } else {
          setTenant(DEFAULT_TENANT);
          setIsDefaultTenant(true);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        setTenant(DEFAULT_TENANT);
        setIsDefaultTenant(true);
      } finally {
        setIsLoading(false);
      }
    };

    detectTenant();
  }, []);

  // Aplicar branding dinamicamente
  useEffect(() => {
    if (!tenant || isLoading) return;

    const cssVarsToClean: string[] = [];

    // Aplicar cores customizadas como CSS variables (HEX → HSL)
    if (tenant.primaryColor) {
      const primaryHsl = hexToHsl(tenant.primaryColor);
      const hoverHsl = adjustHslLightness(primaryHsl, -8);

      document.documentElement.style.setProperty('--primary', primaryHsl);
      document.documentElement.style.setProperty('--primary-hover', hoverHsl);
      document.documentElement.style.setProperty('--ring', primaryHsl);
      document.documentElement.style.setProperty('--sidebar-primary', primaryHsl);
      document.documentElement.style.setProperty('--sidebar-ring', primaryHsl);
      cssVarsToClean.push('--primary', '--primary-hover', '--ring', '--sidebar-primary', '--sidebar-ring');

      // Manter compatibilidade
      document.documentElement.style.setProperty('--tenant-primary', tenant.primaryColor);
      cssVarsToClean.push('--tenant-primary');
    }
    if (tenant.secondaryColor) {
      document.documentElement.style.setProperty('--tenant-secondary', tenant.secondaryColor);
      cssVarsToClean.push('--tenant-secondary');
    }

    // Atualizar favicon
    if (tenant.branding?.favicon_url) {
      const existingLink = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
      if (existingLink) {
        existingLink.href = tenant.branding.favicon_url;
      } else {
        const link = document.createElement('link');
        link.rel = 'icon';
        link.href = tenant.branding.favicon_url;
        document.head.appendChild(link);
      }
    }

    // Atualizar título da página - usar system_name ou nome do tenant
    const pageTitle = tenant.branding?.system_name || tenant.name;
    if (pageTitle && pageTitle !== 'Vendas.Group') {
      document.title = pageTitle;
    }

    // Aplicar tema forçado do parceiro (apenas se user não tem preferência salva)
    const defaultTheme = tenant.branding?.default_theme;
    let previousThemeState: boolean | null = null;
    if (defaultTheme && defaultTheme !== 'system') {
      const userThemePref = safeLocalStorage.getItem('theme');
      if (!userThemePref) {
        previousThemeState = document.documentElement.classList.contains('dark');
        if (defaultTheme === 'dark') {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
    }

    // Cleanup: remover CSS variables customizadas ao desmontar
    return () => {
      cssVarsToClean.forEach(v => document.documentElement.style.removeProperty(v));
      // Restaurar tema anterior se foi alterado
      if (previousThemeState !== null) {
        if (previousThemeState) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
    };
  }, [tenant, isLoading]);

  return (
    <TenantContext.Provider value={{ tenant, tenantId: tenant?.id ?? null, isLoading, isDefaultTenant, error }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

export { DEFAULT_TENANT };
